import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { useCallback, useEffect, useMemo } from "react";
import { getAdminPermissions } from "@/shared/config/adminPermissions";
import { useApi } from "@/shared/utils/request";
import { type CacheSliceData, type CacheSliceKey, cacheSlices } from "./slices";
import type { AuthSliceData } from "./slices/auth";
import type { CacheSliceConfig, CacheSliceState } from "./types";

type SliceConfig<T = unknown> = CacheSliceConfig<T>;

const CACHE_QUERY_ROOT = ["cache"] as const;
const CACHE_SLICE_STALE_MS = 5 * 60 * 1000;
const ANONYMOUS_QUERY_SCOPE = "anonymous";

const defaultAuthData: AuthSliceData = {
	userId: "",
	email: "",
	firstName: null,
	lastName: null,
	displayName: "",
	initials: "?",
	isAdmin: false,
	adminPermissions: {
		overview: false,
		billing: false,
		entries: false,
		calendar: false,
		support: false,
	},
};

function cacheSliceQueryKey(key: CacheSliceKey, userId?: string) {
	return [...CACHE_QUERY_ROOT, key, userId ?? ANONYMOUS_QUERY_SCOPE] as const;
}

function scopedPersistKey(baseKey: string, userId: string) {
	return `${baseKey}:${userId}`;
}

function readPersistedData<T>(
	config: SliceConfig<T>,
	userId: string | undefined,
): { data: T; fetchedAt: number } | undefined {
	if (!config.persistKey || !userId) return undefined;

	try {
		const cached = window.localStorage.getItem(
			scopedPersistKey(config.persistKey, userId),
		);
		if (!cached) return undefined;
		const parsed = JSON.parse(cached) as { data?: T; fetchedAt?: number };
		if (parsed.data === undefined || typeof parsed.fetchedAt !== "number")
			return undefined;
		return {
			data: config.normalize ? config.normalize(parsed.data) : parsed.data,
			fetchedAt: parsed.fetchedAt,
		};
	} catch {
		return undefined;
	}
}

function writePersistedData<T>(
	config: SliceConfig<T>,
	userId: string | undefined,
	data: T | undefined,
	fetchedAt: number,
) {
	if (!config.persistKey || !userId || data === undefined || fetchedAt === 0)
		return;

	try {
		window.localStorage.setItem(
			scopedPersistKey(config.persistKey, userId),
			JSON.stringify({ data, fetchedAt }),
		);
	} catch {
		// localStorage full or unavailable - non-critical
	}
}

function authDataFromUser(
	user: ReturnType<typeof useAuth>["user"],
): AuthSliceData {
	if (!user) return defaultAuthData;

	const email = user.email ?? "";
	const { isAdmin, canView } = getAdminPermissions(email);
	return {
		userId: user.id,
		email,
		firstName: user.firstName ?? null,
		lastName: user.lastName ?? null,
		displayName:
			user.firstName && user.lastName
				? `${user.firstName} ${user.lastName}`
				: email,
		initials:
			user.firstName && user.lastName
				? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
				: (email[0]?.toUpperCase() ?? "?"),
		isAdmin,
		adminPermissions: canView,
	};
}

/** Read a single shared slice through React Query. */
export function useCacheSlice<K extends CacheSliceKey>(
	key: K,
): CacheSliceState<CacheSliceData<K>> {
	const { user, isLoading: isAuthLoading } = useAuth();
	const api = useApi();
	const config = cacheSlices[key] as SliceConfig<CacheSliceData<K>>;
	const userId = user?.id;
	const persisted = useMemo(
		() => readPersistedData(config, userId),
		[config, userId],
	);

	const query = useQuery({
		queryKey: cacheSliceQueryKey(key, userId),
		queryFn: () => config.fetch(api),
		enabled: Boolean(userId) && key !== "auth",
		initialData: persisted?.data,
		initialDataUpdatedAt: persisted?.fetchedAt,
		staleTime: CACHE_SLICE_STALE_MS,
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (key === "auth") return;
		writePersistedData(config, userId, query.data, query.dataUpdatedAt);
	}, [config, key, query.data, query.dataUpdatedAt, userId]);

	if (key === "auth") {
		return {
			data: authDataFromUser(user) as CacheSliceData<K>,
			isLoading: isAuthLoading,
			error: null,
			lastFetchedAt: user ? Date.now() : 0,
		};
	}

	return {
		data: (query.data ?? config.initialValue) as CacheSliceData<K>,
		isLoading: query.isLoading && query.data === undefined,
		error:
			query.error instanceof Error
				? query.error.message
				: query.error
					? "Fetch failed"
					: null,
		lastFetchedAt: query.dataUpdatedAt,
	};
}

/** Access mutation helpers. */
export function useCacheMutations() {
	const { user } = useAuth();
	const api = useApi();
	const queryClient = useQueryClient();
	const userId = user?.id;

	const refresh = useCallback(
		async (key: CacheSliceKey) => {
			if (!userId || key === "auth") return;
			const config = cacheSlices[key] as SliceConfig;
			await queryClient.fetchQuery({
				queryKey: cacheSliceQueryKey(key, userId),
				queryFn: () => config.fetch(api),
				staleTime: 0,
			});
		},
		[api, queryClient, userId],
	);

	const refreshAll = useCallback(async () => {
		await queryClient.invalidateQueries({
			queryKey: CACHE_QUERY_ROOT,
			refetchType: "active",
		});
	}, [queryClient]);

	const optimisticUpdate = useCallback(
		<K extends CacheSliceKey>(
			key: K,
			updater: (prev: CacheSliceData<K>) => CacheSliceData<K>,
		): (() => void) => {
			const queryKey = cacheSliceQueryKey(key, userId);
			const previous = queryClient.getQueryData<CacheSliceData<K>>(queryKey);
			const current =
				previous ?? (cacheSlices[key].initialValue as CacheSliceData<K>);

			queryClient.setQueryData<CacheSliceData<K>>(queryKey, updater(current));

			return () => {
				queryClient.setQueryData<CacheSliceData<K>>(
					queryKey,
					previous ?? current,
				);
			};
		},
		[queryClient, userId],
	);

	return { refresh, refreshAll, optimisticUpdate };
}
